import React, { useCallback, useEffect, useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError, LOGOUT_REASON_KEY } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { REGEXP_ONLY_DIGITS } from "input-otp";
import { LOGIN_OTP } from "@/constants/testIds";
import { ArrowLeft, Clock, Loader2 } from "lucide-react";

const LOGO = "/logo.png";
const BG = "https://images.unsplash.com/photo-1608551283100-6837375103ec?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

// Whether a code-step error ends this sign-in (back to the first step) or
// can be retried in place. The server says "…Please sign in again." when the
// challenge is spent; "Tap Resend or sign in again." means Resend still works.
function otpMustRestart(status, detail) {
  const d = String(detail || "").toLowerCase();
  if (status === 403) return true;                       // account disabled meanwhile
  if (status === 429) return !d.startsWith("please wait"); // too many codes/resends
  return d.includes("sign in again") && !d.includes("resend");
}

export default function Login() {
  const { getLoginConfig, login, requestLoginOtp, verifyLoginOtp, resendLoginOtp, user } = useAuth();
  const location = useLocation();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  // A forced logout (account disabled/blacklisted mid-session) leaves the
  // server's message behind — show it once, then forget it.
  const [error, setError] = useState(() => sessionStorage.getItem(LOGOUT_REASON_KEY) || "");
  const [loading, setLoading] = useState(false);
  useEffect(() => { sessionStorage.removeItem(LOGOUT_REASON_KEY); }, []);

  // Which form: true = mobile/email + code (no password), false = password,
  // null = still asking the server. If it can't be asked, `fallback` is used.
  const [otpLogin, setOtpLogin] = useState(null);
  const [identifier, setIdentifier] = useState("");
  const loadConfig = useCallback(async (fallback) => {
    try {
      const cfg = await getLoginConfig();
      setOtpLogin(!!cfg?.otp_login);
    } catch (e) {
      setOtpLogin(fallback);
    }
  }, [getLoginConfig]);
  useEffect(() => { loadConfig(false); }, [loadConfig]);

  // Code step: the challenge from request-otp (null = first step).
  const [challenge, setChallenge] = useState(null);
  const [code, setCode] = useState("");
  const [notice, setNotice] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendAt, setResendAt] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  const resendIn = Math.max(0, Math.ceil((resendAt - now) / 1000));
  const codeLength = challenge?.code_length || 6;

  useEffect(() => {
    if (!challenge || resendAt <= Date.now()) return;
    const t = setInterval(() => {
      const n = Date.now();
      setNow(n);
      if (n >= resendAt) clearInterval(t);
    }, 1000);
    return () => clearInterval(t);
  }, [challenge, resendAt]);

  const holdResend = (seconds) => {
    const n = Date.now();
    setNow(n);
    setResendAt(n + seconds * 1000);
  };

  const startChallenge = (ch) => {
    setChallenge(ch);
    setCode("");
    holdResend(ch.resend_after ?? 30);
  };

  // Back to the first step. The mobile/email is kept; a password never is.
  const backToStart = (msg = "") => {
    setChallenge(null);
    setCode("");
    setPassword("");
    setNotice("");
    setError(msg);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await login(username, password);
      if (res?.otp_required) {
        startChallenge(res);
        setNotice("");
        setLoading(false);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setLoading(false);
      // Stale config: OTP login was switched on since this page loaded.
      if (err.response?.status === 403 && String(detail || "").startsWith("Password sign-in is turned off")) {
        if (!identifier) setIdentifier(username);
        setPassword("");
        setError("Password sign-in is turned off. Sign in with a one-time code instead.");
        await loadConfig(true);
        return;
      }
      setError(formatApiError(detail) || "Login failed");
    }
  };

  const requestCode = async (e) => {
    e.preventDefault();
    setError("");
    setNotice("");
    setLoading(true);
    try {
      startChallenge(await requestLoginOtp(identifier));
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(formatApiError(detail) || "Could not send the code");
      // Stale config the other way: OTP login was switched off.
      if (err.response?.status === 400 && /not turned on/i.test(String(detail || ""))) await loadConfig(false);
    } finally {
      setLoading(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    if (code.length < codeLength || verifying) return;
    setError("");
    setNotice("");
    setVerifying(true);
    try {
      await verifyLoginOtp(challenge.challenge_id, code);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = formatApiError(detail) || "Verification failed";
      setVerifying(false);
      if (otpMustRestart(err.response?.status, detail)) backToStart(msg);
      else { setError(msg); setCode(""); }
    }
  };

  const resend = async () => {
    setError("");
    setNotice("");
    setResending(true);
    try {
      startChallenge(await resendLoginOtp(challenge.challenge_id));
      setNotice("A new code has been sent. The previous code no longer works.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = formatApiError(detail);
      if (otpMustRestart(err.response?.status, detail)) backToStart(msg);
      else {
        setError(msg);
        const wait = /wait (\d+)s/i.exec(String(detail || ""));
        if (wait) holdResend(Number(wait[1]));
      }
    } finally {
      setResending(false);
    }
  };

  if (user) {
    const dest = location.state?.from?.pathname || "/";
    return <Navigate to={dest} replace />;
  }

  return (
    <div className="flex min-h-screen bg-cream">
      {/* Left: brand visual */}
      <div className="relative hidden w-1/2 lg:block">
        <img src={BG} alt="Dermatrix clinic" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-navy/70" />
        <div className="absolute inset-0 flex flex-col justify-between p-12 text-white">
          <div className="inline-flex w-fit rounded-lg bg-white px-4 py-3 shadow-md">
            <img src={LOGO} alt="Dermatrix" className="h-9 object-contain" />
          </div>
          <div className="max-w-md">
            <h2 className="font-display text-4xl font-semibold leading-tight">
              The command center for your dermatology network.
            </h2>
            <p className="mt-4 text-white/80">
              Manage leads, appointments, franchises and patient conversions across every clinic — from a single, centralized CRM.
            </p>
            <div className="mt-8 flex gap-8">
              <div><p className="font-display text-3xl font-bold text-gold">15+</p><p className="text-sm text-white/70">Clinics</p></div>
              <div><p className="font-display text-3xl font-bold text-gold">50000+</p><p className="text-sm text-white/70">Patients</p></div>
              <div><p className="font-display text-3xl font-bold text-gold">USFDA</p><p className="text-sm text-white/70">Standards</p></div>
            </div>
          </div>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex w-full items-center justify-center px-6 lg:w-1/2">
        <div className="w-full max-w-md">
          <div className="mb-8 flex flex-col items-center lg:items-start">
            <img src={LOGO} alt="Dermatrix" className="mb-6 h-12 object-contain lg:hidden" />
            <h1 className="font-display text-3xl font-semibold text-navy">{challenge ? "Enter login code" : "Welcome back"}</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {challenge ? (challenge.approval ? "Super admin approval is needed to sign in now" : "One more step to sign in")
                : otpLogin ? "Sign in with a one-time code" : "Sign in to your Dermatrix CRM account"}
            </p>
          </div>

          {otpLogin === null ? (
            <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-navy" /></div>
          ) : challenge ? (
            <form onSubmit={verify} className="space-y-5" data-testid={LOGIN_OTP.form}>
              {challenge.approval ? (
                <div className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid={LOGIN_OTP.message}>
                  <Clock className="mt-0.5 h-4 w-4 shrink-0" />
                  <p>{challenge.message}</p>
                </div>
              ) : (
                <p className="text-sm text-ink" data-testid={LOGIN_OTP.message}>{challenge.message}</p>
              )}

              <div className="space-y-2">
                <Label htmlFor="login-otp" className="text-ink">Login code</Label>
                <InputOTP id="login-otp" maxLength={codeLength} value={code} onChange={setCode}
                  pattern={REGEXP_ONLY_DIGITS} inputMode="numeric" autoComplete="one-time-code" autoFocus
                  data-testid={LOGIN_OTP.codeInput}>
                  <InputOTPGroup>
                    {Array.from({ length: codeLength }, (_, i) => (
                      <InputOTPSlot key={i} index={i} className="h-11 w-11 bg-white text-base" />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
                <p className="text-xs text-muted-foreground">
                  The code is valid for {Math.round((challenge.expires_in || 300) / 60)} minutes.
                </p>
              </div>

              {notice && (
                <div className="rounded-lg bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-700" data-testid={LOGIN_OTP.notice}>
                  {notice}
                </div>
              )}
              {error && (
                <div className="rounded-lg bg-red-50 px-4 py-3 text-sm font-medium text-red-700" data-testid={LOGIN_OTP.error}>
                  {error}
                </div>
              )}

              <Button type="submit" disabled={verifying || code.length < codeLength} data-testid={LOGIN_OTP.verifyButton}
                className="h-11 w-full bg-navy text-white hover:bg-navy-hover">
                {verifying ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Verifying…</> : "Verify & sign in"}
              </Button>

              <div className="flex items-center justify-between text-sm">
                <button type="button" onClick={() => backToStart()} disabled={verifying}
                  className="inline-flex items-center font-medium text-navy hover:underline" data-testid={LOGIN_OTP.backLink}>
                  <ArrowLeft className="mr-1 h-3.5 w-3.5" /> Back
                </button>
                <button type="button" onClick={resend} disabled={resendIn > 0 || resending || verifying}
                  className="font-medium text-navy hover:underline disabled:cursor-not-allowed disabled:text-muted-foreground disabled:no-underline"
                  data-testid={LOGIN_OTP.resendButton}>
                  {resending ? "Sending…" : resendIn > 0 ? `Resend code in ${resendIn}s` : "Resend code"}
                </button>
              </div>
            </form>
          ) : otpLogin ? (
            <form onSubmit={requestCode} className="space-y-5" data-testid={LOGIN_OTP.requestForm}>
              <div className="space-y-2">
                <Label htmlFor="identifier" className="text-ink">Mobile number or email</Label>
                <Input id="identifier" value={identifier} onChange={(e) => setIdentifier(e.target.value.replace(/\s/g, ""))}
                  data-testid={LOGIN_OTP.identifierInput} required autoFocus autoComplete="username"
                  className="h-11 bg-white" placeholder="98xxxxxx10 or you@clinic.com" />
              </div>

              {error && (
                <div className="rounded-lg bg-red-50 px-4 py-3 text-sm font-medium text-red-700" data-testid={LOGIN_OTP.requestError}>
                  {error}
                </div>
              )}

              <Button type="submit" disabled={loading || !identifier} data-testid={LOGIN_OTP.requestButton}
                className="h-11 w-full bg-navy text-white hover:bg-navy-hover">
                {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending code…</> : "Send code"}
              </Button>
            </form>
          ) : (
          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-ink">Username</Label>
              <Input id="username" value={username} onChange={(e) => setUsername(e.target.value.replace(/\s/g, ""))}
                data-testid="login-username" required className="h-11 bg-white" placeholder="Email, phone, or ID" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-ink">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value.replace(/\s/g, ""))}
                data-testid="login-password" required className="h-11 bg-white" placeholder="••••••••" />
            </div>

            <div className="flex justify-end">
              <Link to="/forgot-password" className="text-sm font-medium text-navy hover:underline" data-testid="forgot-password-link">
                Forgot password?
              </Link>
            </div>

            {error && (
              <div className="rounded-lg bg-red-50 px-4 py-3 text-sm font-medium text-red-700" data-testid="login-error">
                {error}
              </div>
            )}

            <Button type="submit" disabled={loading} data-testid="login-submit"
              className="h-11 w-full bg-navy text-white hover:bg-navy-hover">
              {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Signing in…</> : "Sign in"}
            </Button>
          </form>
          )}

        </div>
      </div>
    </div>
  );
}
